/**
 * UserDetailModal.jsx — view + edit a single user from the admin dashboard.
 * Backed by:
 *   GET/PATCH /api/subscriptions/admin/users/<id>/
 *   PATCH     /api/subscriptions/admin/users/<id>/subscription/
 *   GET       /api/subscriptions/plans/           (for the plan dropdown)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.16)',
  white:'#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.25)',
  cyan:'#00c8f0', purple:'#b78cff', green:'#2ecc71', red:'#e74c3c', yellow:'#f1c40f',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const STATUS_OPTIONS = ['active', 'trialing', 'past_due', 'cancelled', 'expired'];

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-CA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
}

function Toggle({ label, checked, onChange, disabled }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:8, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .5 : 1 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} style={{ width:15, height:15, accentColor:T.cyan }}/>
      <span style={{ fontSize:12.5, color:T.white }}>{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:T.dim, marginBottom:5 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'8px 11px', borderRadius:7, border:`1px solid ${T.border}`,
  background:T.bg, color:T.white, fontSize:13, outline:'none', boxSizing:'border-box',
  fontFamily:'"DM Sans",system-ui,sans-serif',
};

export default function UserDetailModal({ userId, onClose, onSaved }) {
  const { apiFetch, user: currentUser } = useAuth();

  const [detail, setDetail]       = useState(null);
  const [plans, setPlans]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSub, setSavingSub]         = useState(false);
  const [error, setError]         = useState('');
  const [notice, setNotice]       = useState('');

  const [profileForm, setProfileForm] = useState(null);
  const [subForm, setSubForm]         = useState(null);

  const isSelf = currentUser?.id === userId;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detRes, plansRes] = await Promise.all([
        apiFetch(`/api/subscriptions/admin/users/${userId}/`),
        plans.length ? Promise.resolve(null) : apiFetch('/api/subscriptions/plans/'),
      ]);
      if (detRes?.ok) {
        const d = await detRes.json();
        setDetail(d);
        setProfileForm({
          name: d.name || '', last_name: d.last_name || '', email: d.email || '',
          is_active: d.is_active, is_staff: d.is_staff,
          is_verified: d.is_verified, email_verified: d.email_verified,
        });
        setSubForm(d.subscription ? {
          plan_id: d.subscription.plan_id,
          status: d.subscription.status,
          current_period_end: toLocalInputValue(d.subscription.current_period_end),
          cancel_at_period_end: d.subscription.cancel_at_period_end,
        } : null);
      } else {
        setError('Failed to load user.');
      }
      if (plansRes?.ok) setPlans(await plansRes.json());
    } catch (e) {
      setError('Failed to load user.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, userId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSavingProfile(true);
    setError(''); setNotice('');
    try {
      const res = await apiFetch(`/api/subscriptions/admin/users/${userId}/`, {
        method: 'PATCH',
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save profile.'); return; }
      setDetail(data);
      setNotice('Profile saved.');
      onSaved?.();
    } catch (e) {
      setError('Failed to save profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSubscription = async () => {
    if (!subForm) return;
    setSavingSub(true);
    setError(''); setNotice('');
    try {
      const body = {
        plan_id: subForm.plan_id,
        status: subForm.status,
        current_period_end: subForm.current_period_end ? new Date(subForm.current_period_end).toISOString() : null,
        cancel_at_period_end: subForm.cancel_at_period_end,
      };
      const res = await apiFetch(`/api/subscriptions/admin/users/${userId}/subscription/`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save subscription.'); return; }
      setDetail(data);
      setSubForm({
        plan_id: data.subscription.plan_id,
        status: data.subscription.status,
        current_period_end: toLocalInputValue(data.subscription.current_period_end),
        cancel_at_period_end: data.subscription.cancel_at_period_end,
      });
      setNotice('Subscription updated.');
      onSaved?.();
    } catch (e) {
      setError('Failed to save subscription.');
    } finally {
      setSavingSub(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:100, background:'rgba(0,0,0,.6)',
        display:'flex', alignItems:'flex-start', justifyContent:'center',
        padding:'40px 16px', overflowY:'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:T.bg2, border:`1px solid ${T.borderH}`, borderRadius:14,
          width:'100%', maxWidth:640, fontFamily:'"DM Sans",system-ui,sans-serif',
          boxShadow:'0 20px 60px rgba(0,0,0,.5)',
        }}
      >
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ fontSize:15, fontWeight:800, color:T.white, fontFamily:T.sans }}>
            {loading ? 'Loading…' : (detail?.email || 'User')}
          </div>
          <button onClick={onClose} style={{
            background:'none', border:'none', color:T.muted, fontSize:16, cursor:'pointer', padding:4,
          }}>✕</button>
        </div>

        <div style={{ padding:'18px 20px', maxHeight:'70vh', overflowY:'auto' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'30px', color:T.muted }}>Loading…</div>
          ) : !detail ? (
            <div style={{ textAlign:'center', padding:'30px', color:T.red }}>{error || 'User not found.'}</div>
          ) : (
            <>
              {error && (
                <div style={{ padding:'9px 12px', borderRadius:8, marginBottom:14, background:'rgba(231,76,60,.1)', border:'1px solid rgba(231,76,60,.3)', color:T.red, fontSize:12.5 }}>
                  {error}
                </div>
              )}
              {notice && (
                <div style={{ padding:'9px 12px', borderRadius:8, marginBottom:14, background:'rgba(46,204,113,.1)', border:'1px solid rgba(46,204,113,.3)', color:T.green, fontSize:12.5 }}>
                  {notice}
                </div>
              )}

              {/* Profile */}
              <div style={{ fontSize:12, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                Profile
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Field label="Email">
                  <input style={inputStyle} value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}/>
                </Field>
                <Field label="Name">
                  <input style={inputStyle} value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}/>
                </Field>
                <Field label="Last Name">
                  <input style={inputStyle} value={profileForm.last_name} onChange={e => setProfileForm(f => ({ ...f, last_name: e.target.value }))}/>
                </Field>
                <Field label="Joined">
                  <div style={{ fontSize:12.5, color:T.muted, padding:'8px 0' }}>{fmtDate(detail.date_joined)}</div>
                </Field>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:16, margin:'6px 0 16px' }}>
                <Toggle label="Active" checked={profileForm.is_active} disabled={isSelf} onChange={v => setProfileForm(f => ({ ...f, is_active: v }))}/>
                <Toggle label="Staff (admin access)" checked={profileForm.is_staff} disabled={isSelf} onChange={v => setProfileForm(f => ({ ...f, is_staff: v }))}/>
                <Toggle label="Verified" checked={profileForm.is_verified} onChange={v => setProfileForm(f => ({ ...f, is_verified: v }))}/>
                <Toggle label="Email verified" checked={profileForm.email_verified} onChange={v => setProfileForm(f => ({ ...f, email_verified: v }))}/>
              </div>
              {isSelf && (
                <div style={{ fontSize:11, color:T.dim, marginBottom:12 }}>
                  You can't remove your own Active/Staff status from this panel — use Django admin if you really need to.
                </div>
              )}
              <button onClick={saveProfile} disabled={savingProfile} style={{
                padding:'8px 16px', borderRadius:8, border:'none',
                background:`linear-gradient(135deg,${T.cyan},#0090c0)`, color:'#07080c',
                fontSize:12.5, fontWeight:700, cursor: savingProfile ? 'default' : 'pointer',
                opacity: savingProfile ? .6 : 1, marginBottom:22,
              }}>{savingProfile ? 'Saving…' : 'Save Profile'}</button>

              {/* Subscription */}
              <div style={{ fontSize:12, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
                Subscription
              </div>

              {!subForm ? (
                <div style={{ fontSize:12.5, color:T.muted, marginBottom:16 }}>No subscription record for this user.</div>
              ) : (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <Field label="Plan">
                      <select style={inputStyle} value={subForm.plan_id} onChange={e => setSubForm(f => ({ ...f, plan_id: e.target.value }))}>
                        {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.price_display}/{p.interval})</option>)}
                      </select>
                    </Field>
                    <Field label="Status">
                      <select style={inputStyle} value={subForm.status} onChange={e => setSubForm(f => ({ ...f, status: e.target.value }))}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Renews / Period End">
                      <input type="datetime-local" style={inputStyle} value={subForm.current_period_end} onChange={e => setSubForm(f => ({ ...f, current_period_end: e.target.value }))}/>
                    </Field>
                    <Field label="Cancel at period end">
                      <div style={{ padding:'8px 0' }}>
                        <Toggle label={subForm.cancel_at_period_end ? 'Yes — will cancel' : 'No — will renew'} checked={subForm.cancel_at_period_end} onChange={v => setSubForm(f => ({ ...f, cancel_at_period_end: v }))}/>
                      </div>
                    </Field>
                  </div>

                  <div style={{ fontSize:11, color:T.dim, margin:'2px 0 12px', lineHeight:1.5 }}>
                    This edits the local record only — it does not call Stripe. If this user has a live
                    Stripe subscription ({detail.subscription.stripe_subscription_id || 'none on file'}),
                    the next webhook can overwrite these fields. For refunds or plan changes that should
                    also affect billing, use the Stripe dashboard.
                  </div>

                  <button onClick={saveSubscription} disabled={savingSub} style={{
                    padding:'8px 16px', borderRadius:8, border:'none',
                    background:`linear-gradient(135deg,${T.purple},#8f6fe0)`, color:'#07080c',
                    fontSize:12.5, fontWeight:700, cursor: savingSub ? 'default' : 'pointer',
                    opacity: savingSub ? .6 : 1, marginBottom:22,
                  }}>{savingSub ? 'Saving…' : 'Save Subscription'}</button>
                </>
              )}

              {/* Payment history */}
              <div style={{ fontSize:12, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10, paddingTop:6, borderTop:`1px solid ${T.border}` }}>
                Payment History
              </div>
              {detail.payments.length === 0 ? (
                <div style={{ fontSize:12.5, color:T.muted }}>No payments recorded.</div>
              ) : (
                <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden' }}>
                  {detail.payments.map((p, i) => (
                    <div key={p.id} style={{
                      display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'8px 12px', fontSize:12,
                      borderBottom: i < detail.payments.length - 1 ? `1px solid ${T.border}` : 'none',
                      background: i % 2 ? 'transparent' : 'rgba(255,255,255,.015)',
                    }}>
                      <span style={{ color:T.muted }}>{fmtDate(p.created_at)}</span>
                      <span style={{ color:T.muted }}>{p.description || p.plan_name}</span>
                      <span style={{
                        color: p.status === 'succeeded' ? T.green : p.status === 'failed' ? T.red : T.yellow,
                        fontWeight:700,
                      }}>{p.amount_display}</span>
                      <span style={{ color:T.dim, minWidth:70, textAlign:'right' }}>{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
